/**
 * ProjectSetup.tsx — Project creation and chart-of-accounts file upload
 *
 * Handles: create project, upload the current chart (Oracle EBS), upload the
 * new chart (Oracle Cloud), display what was read, advance to the next phase.
 */

import React, { useState, useRef } from 'react';
import {
  ConversionProject,
  SchemaSnapshot,
  AccountEntity,
  UploadSchemaResult,
  createProject,
  uploadSchema,
  getSchema,
} from '../../services/conversionArchitectAPI';
import {
  c, font, eyebrow, panel, inputStyle, pill,
  btnPrimary, btnSecondary, WORKSTREAMS, workstreamLabel,
} from './tokens';
import Toast from './Toast';

interface ProjectSetupProps {
  projects: ConversionProject[];
  activeProject: ConversionProject | null;
  onProjectSelect: (project: ConversionProject) => void;
  onProjectCreated: (project: ConversionProject) => void;
  onSchemaUploaded: () => void;
  onPhaseComplete: () => void;
}

const STATUS_LABEL: Record<string, string> = {
  draft: 'Getting started',
  mapping: 'Mapping in progress',
  validating: 'Running checks',
  complete: 'Complete',
};

const ProjectSetup: React.FC<ProjectSetupProps> = ({
  projects,
  activeProject,
  onProjectSelect,
  onProjectCreated,
  onSchemaUploaded,
  onPhaseComplete,
}) => {
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newWorkstream, setNewWorkstream] = useState(WORKSTREAMS[0].value);
  const [creating, setCreating] = useState(false);
  const [toast, setToast] = useState('');

  const [uploading, setUploading] = useState<'source' | 'target' | null>(null);
  const [sourceSchema, setSourceSchema] = useState<SchemaSnapshot | null>(null);
  const [targetSchema, setTargetSchema] = useState<SchemaSnapshot | null>(null);
  const [sourceEntities, setSourceEntities] = useState<AccountEntity[]>([]);
  const [targetEntities, setTargetEntities] = useState<AccountEntity[]>([]);
  const [sourceUpload, setSourceUpload] = useState<UploadSchemaResult | null>(null);
  const [targetUpload, setTargetUpload] = useState<UploadSchemaResult | null>(null);

  const sourceFileRef = useRef<HTMLInputElement>(null);
  const targetFileRef = useRef<HTMLInputElement>(null);

  // Load existing schemas when project is selected
  React.useEffect(() => {
    if (activeProject) {
      if (activeProject.source_schema_id) loadSchema('source');
      if (activeProject.target_schema_id) loadSchema('target');
    }
  }, [activeProject?.id]);

  const loadSchema = async (role: 'source' | 'target') => {
    if (!activeProject) return;
    try {
      const data = await getSchema(activeProject.id, role);
      if (role === 'source') {
        setSourceSchema(data.schema);
        setSourceEntities(data.entities);
      } else {
        setTargetSchema(data.schema);
        setTargetEntities(data.entities);
      }
    } catch {
      // Schema not loaded yet — OK
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const project = await createProject({
        name: newName.trim(),
        workstream: newWorkstream,
      });
      onProjectCreated(project);
      setShowCreate(false);
      setNewName('');
      setToast(`Project "${project.name}" created`);
    } catch (err: any) {
      console.error('Failed to create project:', err);
      setToast(err.response?.data?.error || 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const handleFileUpload = async (role: 'source' | 'target', file: File) => {
    if (!activeProject) return;
    setUploading(role);
    try {
      const result = await uploadSchema(activeProject.id, role, file, file.name);
      if (role === 'source') {
        setSourceSchema(result.schema);
        setSourceUpload(result);
      } else {
        setTargetSchema(result.schema);
        setTargetUpload(result);
      }
      setToast(`${result.entity_count} accounts read from ${file.name}`);
      onSchemaUploaded();
      await loadSchema(role);
    } catch (err: any) {
      console.error(`Failed to upload ${role} schema:`, err);
      setToast(err.response?.data?.error || `Failed to upload the ${role === 'source' ? 'current' : 'new'} chart`);
    } finally {
      setUploading(null);
    }
  };

  const bothUploaded = !!(sourceSchema && targetSchema);

  // Detected columns for chip display — prefer the fresh upload result, fall
  // back to the stored schema's column mapping.
  const detectedColumns = (upload: UploadSchemaResult | null, schema: SchemaSnapshot | null): Array<[string, string]> => {
    const detected = upload?.column_detection?.detected;
    if (detected && typeof detected === 'object' && Object.keys(detected).length > 0) {
      return Object.entries(detected).map(([k, v]) => [k, String(v)]);
    }
    if (schema?.column_mapping && Object.keys(schema.column_mapping).length > 0) {
      return Object.entries(schema.column_mapping).map(([k, v]) => [k, String(v)]);
    }
    return [];
  };

  const renderConfirmationNotice = (upload: UploadSchemaResult | null) => {
    if (!upload || !upload.needs_confirmation) return null;
    const unmatched = upload.column_detection?.unmatched_columns || [];
    return (
      <div
        style={{
          background: c.amber.fillSoft,
          border: `1px solid ${c.amber.border}`,
          borderRadius: 8,
          padding: '10px 14px',
          marginTop: 12,
        }}
      >
        <div style={{ fontSize: 12.5, fontWeight: 700, color: c.amber.text, marginBottom: 4 }}>
          Check how the columns were read
        </div>
        {upload.question && (
          <div style={{ fontSize: 12.5, color: c.amber.text2, lineHeight: 1.5 }}>
            {upload.question.title || upload.question.description}
          </div>
        )}
        {unmatched.length > 0 && (
          <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: c.amber.text }}>Columns we could not place:</span>
            {unmatched.map(col => (
              <span key={col} style={{ ...pill(c.amber.fill, c.amber.text, c.amber.border), fontFamily: font.mono, fontSize: 10 }}>
                {col}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderSchemaSummary = (
    role: 'source' | 'target',
    schema: SchemaSnapshot,
    entities: AccountEntity[],
    upload: UploadSchemaResult | null,
    fileRef: React.RefObject<HTMLInputElement>,
  ) => {
    const accent = role === 'source' ? c.blue.text2 : c.green.text2;
    const columns = detectedColumns(upload, schema);
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
          <span style={{ fontFamily: font.mono, fontSize: 22, fontWeight: 800, color: accent }}>
            {schema.entity_count}
          </span>
          <span style={{ fontSize: 12.5, color: c.textBody, fontWeight: 600 }}>accounts read</span>
        </div>
        <div style={{ fontFamily: font.mono, fontSize: 11, color: c.textSecondary, marginBottom: 10 }}>
          {schema.label}
        </div>

        {columns.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            <div style={{ ...eyebrow, marginBottom: 6 }}>Columns detected</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {columns.map(([field, col]) => (
                <span
                  key={field}
                  title={`Read "${col}" as ${field.replace(/_/g, ' ')}`}
                  style={{ ...pill(c.fill2, c.tableLabel), fontFamily: font.mono, fontSize: 10 }}
                >
                  {col}
                </span>
              ))}
            </div>
          </div>
        )}

        {renderConfirmationNotice(upload)}

        {entities.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Code', 'Name', 'Type'].map(h => (
                    <th
                      key={h}
                      style={{
                        textAlign: 'left', padding: '6px 8px', fontSize: 10, fontWeight: 800,
                        textTransform: 'uppercase', letterSpacing: '0.8px', color: c.textSecondary,
                        borderBottom: `1px solid ${c.border}`,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {entities.slice(0, 5).map(e => (
                  <tr key={e.id}>
                    <td style={{ padding: '6px 8px', fontFamily: font.mono, fontSize: 12, color: c.textSecondary, borderBottom: `1px solid ${c.hairline}` }}>
                      {e.account_code}
                    </td>
                    <td style={{ padding: '6px 8px', fontSize: 12.5, color: c.textBody, borderBottom: `1px solid ${c.hairline}` }}>
                      {e.account_name}
                    </td>
                    <td style={{ padding: '6px 8px', fontSize: 12, color: c.textSecondary, borderBottom: `1px solid ${c.hairline}` }}>
                      {e.account_type}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {entities.length > 5 && (
              <p style={{ fontSize: 11.5, color: c.textTertiary, margin: '8px 0 0' }}>
                + {entities.length - 5} more accounts
              </p>
            )}
          </div>
        )}

        <button
          type="button"
          style={{ ...btnSecondary(), marginTop: 12, fontSize: 12, padding: '6px 14px' }}
          onClick={() => fileRef.current?.click()}
        >
          Replace file
        </button>
      </div>
    );
  };

  const renderDropzone = (role: 'source' | 'target', fileRef: React.RefObject<HTMLInputElement>) => {
    const isUploading = uploading === role;
    return (
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={isUploading}
        style={{
          width: '100%',
          background: 'transparent',
          border: `2px dashed ${c.borderControl}`,
          borderRadius: 10,
          padding: '34px 20px',
          textAlign: 'center',
          cursor: isUploading ? 'default' : 'pointer',
          fontFamily: font.ui,
        }}
      >
        {isUploading ? (
          <span style={{ fontSize: 13, color: c.textSecondary }}>Reading the file…</span>
        ) : (
          <>
            <div style={{ fontSize: 13, fontWeight: 700, color: c.textBody }}>
              Click to upload a file
            </div>
            <div style={{ fontSize: 11.5, color: c.textTertiary, marginTop: 4 }}>CSV or Excel (.xlsx)</div>
          </>
        )}
      </button>
    );
  };

  // --- Project list view ---
  if (!activeProject) {
    return (
      <div style={{ fontFamily: font.ui }}>
        <div style={panel}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: c.textPrimary }}>Conversion projects</h2>
            <button type="button" style={btnPrimary()} onClick={() => setShowCreate(true)}>
              + New project
            </button>
          </div>

          {showCreate && (
            <div style={{ background: c.fill1, border: `1px solid ${c.border}`, borderRadius: 10, padding: 16, marginBottom: 16 }}>
              <div style={{ marginBottom: 12 }}>
                <label htmlFor="ca-new-project-name" style={{ display: 'block', fontSize: 12, fontWeight: 700, color: c.tableLabel, marginBottom: 5 }}>
                  Project name
                </label>
                <input
                  id="ca-new-project-name"
                  type="text"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  placeholder="e.g., SMBC GL Migration Phase 3.5"
                  autoFocus
                  style={inputStyle}
                />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label htmlFor="ca-new-project-workstream" style={{ display: 'block', fontSize: 12, fontWeight: 700, color: c.tableLabel, marginBottom: 5 }}>
                  Workstream
                </label>
                <select
                  id="ca-new-project-workstream"
                  value={newWorkstream}
                  onChange={e => setNewWorkstream(e.target.value)}
                  style={inputStyle}
                >
                  {WORKSTREAMS.map(w => (
                    <option key={w.value} value={w.value}>{w.label}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  type="button"
                  style={btnPrimary(creating || !newName.trim())}
                  onClick={handleCreate}
                  disabled={creating || !newName.trim()}
                >
                  {creating ? 'Creating…' : 'Create project'}
                </button>
                <button type="button" style={btnSecondary()} onClick={() => setShowCreate(false)}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {projects.length === 0 && !showCreate ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: c.textSecondary, fontSize: 13 }}>
              No conversion projects yet. Create one to get started.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
              {projects.map(project => (
                <button
                  key={project.id}
                  type="button"
                  onClick={() => onProjectSelect(project)}
                  style={{
                    textAlign: 'left',
                    background: c.card,
                    border: `1px solid ${c.border}`,
                    borderRadius: 10,
                    padding: 16,
                    cursor: 'pointer',
                    fontFamily: font.ui,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
                    <span style={{ fontSize: 15, fontWeight: 800, color: c.textPrimary }}>{project.name}</span>
                    <span style={pill(c.fill2, c.tableLabel)}>
                      {STATUS_LABEL[project.status] || project.status}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: c.textSecondary, display: 'flex', gap: 12, marginBottom: 8 }}>
                    <span>{workstreamLabel(project.workstream)}</span>
                    <span>Created {new Date(project.created_at).toLocaleDateString()}</span>
                  </div>
                  {project.summary_stats && (
                    <div style={{ display: 'flex', gap: 12, fontSize: 11.5, color: c.textBody, borderTop: `1px solid ${c.hairline}`, paddingTop: 8 }}>
                      <span style={{ fontFamily: font.mono }}>{project.summary_stats.total_rules} mappings</span>
                      {project.summary_stats.questions_open > 0 ? (
                        <span style={{ color: c.amber.text2, fontWeight: 700 }}>
                          {project.summary_stats.questions_open} waiting on you
                        </span>
                      ) : (
                        <span style={{ color: c.green.text2, fontWeight: 700 }}>No open decisions</span>
                      )}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
        <Toast message={toast} onClose={() => setToast('')} />
      </div>
    );
  }

  // --- Active project: upload the two charts ---
  return (
    <div style={{ fontFamily: font.ui }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Source chart (Oracle EBS) */}
        <div
          style={{
            background: c.blue.fillSoft,
            border: `1px dashed ${c.blue.border2}`,
            borderLeft: `4px solid ${c.blue.text2}`,
            borderRadius: 12,
            padding: 20,
          }}
        >
          <div style={{ ...eyebrow, color: c.blue.text, marginBottom: 6 }}>
            Upload the current chart (Oracle EBS)
          </div>
          <p style={{ fontSize: 12.5, color: c.textBody, margin: '0 0 14px', lineHeight: 1.5 }}>
            The chart of accounts the business uses today. CSV or Excel (.xlsx).
          </p>
          {sourceSchema
            ? renderSchemaSummary('source', sourceSchema, sourceEntities, sourceUpload, sourceFileRef)
            : renderDropzone('source', sourceFileRef)}
          <input
            ref={sourceFileRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            aria-label="Upload the current chart of accounts file"
            style={{ display: 'none' }}
            onChange={e => {
              const file = e.target.files?.[0];
              if (file) handleFileUpload('source', file);
              e.target.value = '';
            }}
          />
        </div>

        {/* Target chart (Oracle Cloud) */}
        <div
          style={{
            background: c.green.fillSoft,
            border: `1px dashed ${c.green.border2}`,
            borderLeft: `4px solid ${c.green.text2}`,
            borderRadius: 12,
            padding: 20,
          }}
        >
          <div style={{ ...eyebrow, color: c.green.text, marginBottom: 6 }}>
            Upload the new chart (Oracle Cloud)
          </div>
          <p style={{ fontSize: 12.5, color: c.textBody, margin: '0 0 14px', lineHeight: 1.5 }}>
            The chart of accounts the business is moving to. CSV or Excel (.xlsx).
          </p>
          {targetSchema
            ? renderSchemaSummary('target', targetSchema, targetEntities, targetUpload, targetFileRef)
            : renderDropzone('target', targetFileRef)}
          <input
            ref={targetFileRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            aria-label="Upload the new chart of accounts file"
            style={{ display: 'none' }}
            onChange={e => {
              const file = e.target.files?.[0];
              if (file) handleFileUpload('target', file);
              e.target.value = '';
            }}
          />
        </div>
      </div>

      {/* Advance button */}
      {bothUploaded && (
        <div style={{ marginTop: 18, display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            style={{ ...btnPrimary(), fontSize: 14, padding: '11px 22px' }}
            onClick={onPhaseComplete}
          >
            See what changed in the structure →
          </button>
        </div>
      )}

      <Toast message={toast} onClose={() => setToast('')} />
    </div>
  );
};

export default ProjectSetup;
